import * as React from 'react'
import * as PropTypes from 'prop-types'
import ReactDOM from 'react-dom'
import styled, { css } from 'styled-components'
import { Fade } from 'react-reveal'

import Icon from '../Icon/Icon'

import Loader from '../../../assets/img/loader.gif'

import media from '../../../services/generateMediaQueries'

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${props => props.zIndex || 10000};
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.8);
  display: ${({ show }) => (show ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;

  & .react-reveal {
    ${media.lessThan('small')`
      height: 100vh;
    `}
  }
`

const ModalContent = styled.div`
  position: relative;
  background: white;
  max-width: 600px;
  margin: 20px auto;
  padding: 0;
  height: auto;
  text-overflow: wrap;
  overflow: hidden;
  border-radius: var(--border-radius-container);
  transition: all var(--animation-duration) var(--animation-easing);

  ${media.lessThan('small')`
    margin: 0;
    top: 0;
    right: 0;
    height: 100% !important;
    border-radius: 0;
    max-width: 100% !important;
    max-height: unset;
  `}
`

export const DefaultCloseButton = styled.button`
  position: absolute;
  border: 0;
  background-color: transparent;
  top: 15px;
  right: 15px;
  font-size: 30px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.5);
  width: 40px;
  height: 40px;
  padding: 5px;
  border-radius: 100%;
  outline: none;

  ${props =>
    props.zIndex &&
    css`
      z-index: ${props.zIndex + 1};
    `}

  > svg {
    vertical-align: baseline;
  }
`

const LoadingContainer = styled.div`
  min-height: 20rem;
  display: flex;
  align-items: center;
  justify-content: space-around;
`

const LoadingIcon = styled.img.attrs({ src: Loader })`
  height: 5rem;
  width: 5rem;
`

class Modal extends React.Component {
  constructor(props) {
    super(props)

    const { id } = this.props

    this.modalPortalId = `modal-portal-${id}`
  }

  componentWillMount() {
    if (
      this.canUseDOM() &&
      !window.document.getElementById(this.modalPortalId)
    ) {
      const rootElement = window.document.getElementById(
        process.env.REACT_APP_ROOT_ELEMENT_ID,
      )
      const modalPortal = window.document.createElement('div')
      modalPortal.id = this.modalPortalId

      try {
        rootElement.parentNode.insertBefore(
          modalPortal,
          rootElement.nextSibling,
        )
      } catch (error) {}
    }
  }

  componentDidMount() {
    const { show } = this.props

    if (show) {
      this.displayModal(show)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { show } = this.props
    const { show: prevShow } = prevProps

    if (show !== prevShow) {
      this.displayModal(show)
    }
  }

  displayModal(show) {
    const display =
      show ||
      Boolean(
        window.document.querySelector(
          `div:not(#${this.modalPortalId}) > .modal.open`,
        ),
      )

    document
      .querySelector('html')
      .setAttribute('class', display ? 'sticky-modal-open' : '')
    document
      .querySelector('body')
      .setAttribute('class', display ? 'sticky-modal-open' : '')
  }

  canUseDOM() {
    return !!(
      typeof window !== 'undefined' &&
      window.document &&
      window.document.createElement
    )
  }

  render() {
    const {
      handleClose,
      show,
      loading,
      children,
      CloseButton,
      zIndex,
      hasCloseButton = true,
      ...restProps
    } = this.props
    const modal = (
      <ModalWrapper
        onClick={handleClose}
        className={`modal ${show ? 'open' : ''}`}
        show={show}
        zIndex={zIndex}
      >
        {!!show && (
          <Fade bottom distance="1rem" duration={300}>
            <ModalContent {...restProps} onClick={e => e.stopPropagation()}>
              {!!hasCloseButton && (
                <DefaultCloseButton onClick={handleClose} zIndex={zIndex}>
                  <Icon name="x" />
                </DefaultCloseButton>
              )}
              {loading ? (
                <LoadingContainer>
                  <LoadingIcon />
                </LoadingContainer>
              ) : (
                children
              )}
            </ModalContent>
          </Fade>
        )}
      </ModalWrapper>
    )

    if (!this.canUseDOM()) {
      return <></>
    }

    const portalElement = document.getElementById(this.modalPortalId)

    return portalElement ? (
      ReactDOM.createPortal(modal, document.getElementById(this.modalPortalId))
    ) : (
      <></>
    )
  }
}

Modal.defaultProps = {
  CloseButton: null,
}

Modal.propTypes = {
  id: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.array])
    .isRequired,
  CloseButton: PropTypes.element,
}

export default Modal
